import type {ReactNode} from 'react';

import {cn} from '@/lib/utils';

interface BannerProps {
  /** Which edge to pin to. The border is drawn on the inward-facing side. */
  position: 'top' | 'bottom';
  /** The message. */
  children: ReactNode;
  /** Buttons or links, laid out at the trailing edge. */
  actions: ReactNode;
  /** ARIA role, when the banner announces something rather than just sitting there. */
  role?: 'status' | 'alert';
}

/**
 * The shell shared by the app's page-level notices (`ConsentBanner`,
 * `UpdatePrompt`): a full-width bar pinned to one edge, with a message and a
 * trailing action group that stack on narrow screens.
 *
 * `z-40` keeps every notice *below* dialogs, sheets and drawers (all `z-50`):
 * those are transient and modal, and a persistent notice covering their buttons
 * would be worse than waiting behind them.
 */
export function Banner({position, children, actions, role}: BannerProps) {
  return (
    <div
      role={role}
      className={cn(
        'fixed inset-x-0 z-40 bg-background p-4 shadow-lg',
        position === 'top' ? 'top-0 border-b' : 'bottom-0 border-t',
      )}
    >
      <div className="mx-auto flex max-w-6xl flex-col items-center justify-between gap-4 sm:flex-row">
        <div className="flex-1 text-sm text-muted-foreground">{children}</div>
        <div className="flex shrink-0 gap-2">{actions}</div>
      </div>
    </div>
  );
}
